/**
 * Document Confirm Gating — analytics trail for admin overrides.
 *
 * When an admin confirms an order whose billing party's required documentation
 * isn't on file yet, we record one `change_events` row so the override is
 * traceable in external analytics (the same table/replication path the
 * trip pivot reasons use). Overrides only — non-admin blocks write nothing.
 */
import type { ChangeEventInput } from "@/lib/change-events";

/** Acknowledged override carried from the confirm dialog into the mutation. */
export interface ConfirmDocsOverrideAck {
  /** Human labels of the docs that were missing at confirm time. */
  missing: string[];
  /** Billing party name, when known. */
  customerName?: string | null;
}

export interface BuildConfirmDocsOverrideEventArgs {
  orderId: string;
  tenantId: string;
  eapiTripId?: string | null;
  ack: ConfirmDocsOverrideAck;
  /** Order status before this write (null for a brand-new order). */
  previousStatus?: string | null;
}

/**
 * Build the `change_events` row for an acknowledged override.
 * Returns `null` when there is nothing meaningful to record.
 */
export function buildConfirmDocsOverrideEvent(
  args: BuildConfirmDocsOverrideEventArgs,
): ChangeEventInput | null {
  const missing = (args.ack?.missing ?? []).filter(Boolean);
  if (!args.orderId || !args.tenantId || missing.length === 0) return null;
  return {
    orderId: args.orderId,
    tenantId: args.tenantId,
    eapiTripId: args.eapiTripId ?? null,
    eventType: "confirm_docs_override",
    reason: "documentation_override",
    previousValue: { status: args.previousStatus ?? null },
    newValue: {
      status: "confirmed",
      missing,
      customer_name: args.ack.customerName ?? null,
    },
  };
}
